///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { sendCameraCommand, setAnimatedCameraTilt, setCameraBearing, setClockCurrentTime, setClockState } from "@/redux/earthBrowserSlice"
import { appStore, useAppDispatch } from "@/redux/store"
import { saveCurrentView } from "@/utils/savedViews"
import { useEffect } from "react"

/** True when a key event originated in a text field or control that owns its keys */
export function isEditableTarget(target: EventTarget | null): boolean {
	const el = target as HTMLElement | null
	return !!el && (
		el.tagName === "INPUT" ||
		el.tagName === "TEXTAREA" ||
		el.tagName === "SELECT" ||
		el.tagName === "BUTTON" ||
		el.isContentEditable
	)
}

/**
 * Global keyboard shortcuts for time and camera control:
 * - Space: play/pause the animation (from LIVE, play starts animating)
 * - Left/Right arrows: step time by 1 hour (Shift: 10 minutes)
 * - N: face north (or flatten tilt if already facing north)
 * - + / -: zoom in / out
 * - Cmd/Ctrl-D: save the current view (bookmark)
 *
 * Window-relative time shortcuts (Home/End edges, , . frame step) are
 * handled by TimelineBar, which owns the anchored window and frame times.
 *
 * Shortcuts are ignored while typing in inputs or when modifier keys are held.
 */
export function useTimeCameraShortcuts() {
	const dispatch = useAppDispatch()

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Cmd/Ctrl-D: save the current view (replaces browser bookmark)
			if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === "d" || e.key === "D")) {
				e.preventDefault()
				void saveCurrentView()
				return
			}

			if (e.metaKey || e.ctrlKey || e.altKey) return
			if (isEditableTarget(e.target)) return

			const state = appStore.getState().earthbrowser

			switch (e.key) {
				case " ": {
					e.preventDefault()
					// Same rule as the bar's play button: only animation toggles
					const next = state.clockState === "animating" ? "paused" : "animating"
					dispatch(setClockState(next))
					break
				}
				case "ArrowLeft":
				case "ArrowRight": {
					e.preventDefault()
					const stepMs = (e.shiftKey ? 10 * 60 : 60 * 60) * 1000
					if (state.clockState !== "paused") {
						dispatch(setClockState("paused"))
					}
					const delta = e.key === "ArrowRight" ? stepMs : -stepMs
					dispatch(setClockCurrentTime(state.clockCurrentTime + delta))
					break
				}
				case "n":
				case "N": {
					// Same behavior as clicking the compass: face north first,
					// then flatten tilt if already facing north
					if (Math.abs(state.cameraState.bearing) < 0.01) {
						dispatch(setAnimatedCameraTilt(0))
					} else {
						dispatch(setCameraBearing(0))
					}
					break
				}
				case "+":
				case "=":
					dispatch(sendCameraCommand({ command: "zoom/in" }))
					break
				case "-":
				case "_":
					dispatch(sendCameraCommand({ command: "zoom/out" }))
					break
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [dispatch])
}
